import React, { useMemo } from "react";
import { Bot, User } from "lucide-react";
import { useI18n } from "../lib/i18n";
import SourceCitation from "./SourceCitation";
import FeedbackWidget from "./FeedbackWidget";

/**
 * Render AI answer with inline citations [1], [2] converted to clickable chips.
 */
function renderAnswer(text, onCitationClick) {
  if (!text) return null;
  const parts = text.split(/(\[\d+\])/g);
  return parts.map((part, idx) => {
    const m = /^\[(\d+)\]$/.exec(part);
    if (m) {
      const n = parseInt(m[1], 10);
      return (
        <button
          key={idx}
          type="button"
          className="lex-citation"
          onClick={() => onCitationClick && onCitationClick(n)}
          data-testid={`citation-chip-${n}`}
        >
          [{n}]
        </button>
      );
    }
    // Lightweight markdown: **bold**, # headings, - lists
    return <FormattedText key={idx} text={part} />;
  });
}

function FormattedText({ text }) {
  // Split into blocks by double newline
  const blocks = useMemo(() => text.split(/\n{2,}/), [text]);
  return (
    <>
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        if (!trimmed) return null;
        if (trimmed.startsWith("### ")) {
          return <h3 key={i}>{inline(trimmed.slice(4))}</h3>;
        }
        if (trimmed.startsWith("## ")) {
          return <h2 key={i}>{inline(trimmed.slice(3))}</h2>;
        }
        if (trimmed.startsWith("# ")) {
          return <h1 key={i}>{inline(trimmed.slice(2))}</h1>;
        }
        const lines = trimmed.split(/\n/);
        const isList = lines.every((l) => /^\s*[-*]\s+/.test(l));
        if (isList) {
          return (
            <ul key={i}>
              {lines.map((l, k) => (
                <li key={k}>{inline(l.replace(/^\s*[-*]\s+/, ""))}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i}>
            {lines.map((l, k) => (
              <React.Fragment key={k}>
                {inline(l)}
                {k < lines.length - 1 ? <br /> : null}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

function inline(s) {
  // Bold **x**
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(p);
    return m ? <strong key={i}>{m[1]}</strong> : <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

export default function MessageBubble({ role, text, sources = [], onCitationClick, queryLogId, compact = false }) {
  const { t } = useI18n();
  
  const bubbleClasses = compact
    ? "mb-3 animate-fade-in"
    : "mb-5 animate-fade-in";
  
  const contentClasses = compact
    ? "max-w-[85%] rounded-sm px-3 py-2 shadow-sm text-sm"
    : "max-w-[85%] bg-primary text-white rounded-sm px-4 py-3 shadow-sm";

  const avatarSize = compact ? 7 : 9;
  const avatarClasses = compact
    ? `w-${avatarSize} h-${avatarSize} bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0`
    : `w-${avatarSize} h-${avatarSize} bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0`;

  if (role === "user") {
    return (
      <div
        className={`flex justify-end gap-3 ${bubbleClasses}`}
        data-testid="user-message"
      >
        <div className={`${contentClasses} bg-primary text-white`}>
          <p className={`${compact ? 'text-sm' : 'text-[15px]'} leading-relaxed`}>{text}</p>
        </div>
        <div className={avatarClasses}>
          <User size={compact ? 14 : 16} />
        </div>
      </div>
    );
  }

  // ai bubble
  // split 'Sources:' section out of the markdown — we'll render it via the sources prop
  const mainText = stripSources(text);

  return (
    <div className={`flex gap-3 ${bubbleClasses}`} data-testid="ai-message">
      <div className={avatarClasses}>
        <Bot size={compact ? 14 : 16} />
      </div>
      <div className={`flex-1 min-w-0 max-w-[90%] bg-white border border-primary/10 border-l-4 border-l-primary rounded-sm p-4 shadow-sm ${compact ? 'p-3 text-sm' : 'p-5 shadow-sm'}`}>
        <div className={`lex-prose ${compact ? 'text-sm' : 'text-[15px]'} text-foreground/85`}>
          {renderAnswer(mainText, onCitationClick)}
        </div>

        {sources.length > 0 && !compact && (
          <div className="mt-5 pt-4 border-t border-primary/10">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
                {t("chat.sources")}
              </span>
              <span className="font-mono text-[11px] text-foreground/50">
                ({sources.length})
              </span>
            </div>
            <div className="space-y-1">
              {sources.map((s, i) => (
                <SourceCitation
                  key={`${s.reference}-${i}`}
                  source={s}
                  index={i + 1}
                />
              ))}
            </div>
          </div>
        )}

        {queryLogId && !compact && <FeedbackWidget queryLogId={queryLogId} />}
      </div>
    </div>
  );
}

function stripSources(text) {
  if (!text) return "";
  // Strip any trailing block looking like a sources/references list so we
  // only render our own curated list from real chunks.
  let cleaned = text;
  // Case 1: explicit "Sources:" or "Références:" heading
  const idx = cleaned.search(/\n\s*(Sources\s*:?|Références\s*:?|References\s*:?)\s*\n/i);
  if (idx >= 0) cleaned = cleaned.slice(0, idx).trim();
  // Case 2: trailing bullet-list of "[n]" style references
  cleaned = cleaned.replace(/(\n\s*\[\d+\][^\n]*(?:\n[^\n]*)*)+\s*$/m, "").trim();
  return cleaned;
}
